import type { PrV2RepoFileRequest } from '@/lib/pr-v2/code-changes/types';

// GitHub repository identities are case-insensitive; every PR surface and its
// mutations must address the same cache entry regardless of URL casing.
export const prV2QueryKeyPrefix = (owner: string, repo: string, number: string) =>
  ['pr-v2', owner.toLowerCase(), repo.toLowerCase(), number] as const;

export const prV2SummaryQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'summary'] as const;

export const prV2AiAttributionQueryKey = (
  owner: string,
  repo: string,
  number: string,
  headSha?: string | null,
) => [...prV2QueryKeyPrefix(owner, repo, number), 'ai-attribution', headSha ?? null] as const;

export const prV2DemoQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'demo'] as const;

export const prV2StackQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'stack'] as const;

export const prV2VersionsQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'versions'] as const;

/**
 * The labels a repository offers. Keyed by repository rather than pull request, so every pull
 * request in it shares one cached list.
 */
export const prV2AvailableLabelsQueryKey = (owner: string, repo: string) =>
  ['pr-v2-available-labels', owner.toLowerCase(), repo.toLowerCase()] as const;

export const prV2DiscussionQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'discussion'] as const;

export const prV2MergeabilityCapabilitiesQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'mergeability-capabilities'] as const;

export const prV2ReviewsQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'reviews'] as const;

export const prV2CommentsQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'comments'] as const;

export const prV2ChecksQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'checks'] as const;

export const prV2CubicReviewSummaryQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'cubic-review-summary'] as const;

export const prV2ReviewThreadsQueryKey = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'review-threads'] as const;

export const prV2CodeChangesQueryKeyPrefix = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'code-changes'] as const;

export const prV2OrderedFilesQueryKeyPrefix = (owner: string, repo: string, number: string) =>
  [...prV2QueryKeyPrefix(owner, repo, number), 'ordered-files'] as const;

// v2: file-review decisions include line anchors so inline glyphs can render.
const PR_V2_FILE_DESCRIPTION_QUERY_KEY_VERSION = 'v2';

export const prV2FileDescriptionQueryKeyPrefix = (owner: string, repo: string, number: string) =>
  [
    ...prV2QueryKeyPrefix(owner, repo, number),
    'file-description',
    PR_V2_FILE_DESCRIPTION_QUERY_KEY_VERSION,
  ] as const;

const comparisonShaQueryPart = (headSha?: string | null, baseSha?: string | null) =>
  ({
    headSha: headSha ?? null,
    baseSha: baseSha ?? null,
  }) as const;

export const prV2CodeChangesQueryKey = (
  owner: string,
  repo: string,
  number: string,
  headSha?: string | null,
  baseSha?: string | null,
) =>
  [
    ...prV2CodeChangesQueryKeyPrefix(owner, repo, number),
    comparisonShaQueryPart(headSha, baseSha),
  ] as const;

export const prV2OrderedFilesQueryKey = (
  owner: string,
  repo: string,
  number: string,
  headSha?: string | null,
  baseSha?: string | null,
) =>
  [
    ...prV2OrderedFilesQueryKeyPrefix(owner, repo, number),
    comparisonShaQueryPart(headSha, baseSha),
  ] as const;

export const prV2FileDescriptionQueryKey = (
  owner: string,
  repo: string,
  number: string,
  headSha?: string | null,
  baseSha?: string | null,
) =>
  [
    ...prV2FileDescriptionQueryKeyPrefix(owner, repo, number),
    comparisonShaQueryPart(headSha, baseSha),
  ] as const;

/**
 * A repository file read at the comparison the diff shows. It sits outside the code-changes prefix
 * because comment mutations and realtime sync invalidate that prefix, which would refetch an open
 * file after every comment.
 */
export const prV2RepoFileQueryKey = (
  owner: string,
  repo: string,
  number: string,
  request: PrV2RepoFileRequest,
  headSha: string | null,
  baseSha: string | null,
) =>
  [
    ...prV2QueryKeyPrefix(owner, repo, number),
    'repo-file',
    request.side,
    headSha,
    baseSha,
    request.specifier,
    request.fromPath,
  ] as const;

export const prV2FileContentQueryKey = (
  owner: string,
  repo: string,
  number: string,
  headSha?: string | null,
  baseSha?: string | null,
  filename?: string | null,
  sha?: string | null,
  previousFilename?: string | null,
) =>
  [
    ...prV2CodeChangesQueryKeyPrefix(owner, repo, number),
    'file',
    comparisonShaQueryPart(headSha, baseSha),
    {
      filename: filename ?? null,
      sha: sha ?? null,
      previousFilename: previousFilename ?? null,
    },
  ] as const;
